import { useEffect, useRef, useState } from 'react';

import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Avatar } from 'primereact/avatar';
import { Badge } from 'primereact/badge';
import { Dialog } from 'primereact/dialog';

import { defaultPageRowOptions, defaultRowOptions, paginatorLinks, showingEntries, SkeletonbodyTemplate, Skeletonitems } from '../../../appconfig/Settings';
import AppBreadcrumb from '../../../components/CustomeBreadcrumb/AppBreadcrumb';
import { getAllDetails, postAllData } from '../../../service/ProductService';
import { ADD_UPDATE_USER, CHANGE_STATUS_USER, LIST_USER } from '../../../config/ApiConstant';
import { UserColumns } from '../../../appconfig/DatatableSetting';
import { AddUpdateUserValidate } from '../../../config/Validate';

export const Users = () => {
    document.title = 'Users | Karan Enterprise';

    const toast = useRef<any>(null);

    const [globalFilter, setGlobalFilter] = useState<any>(null);
    const [userList, setUserList] = useState<any>([]);
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [userData, setUserData] = useState<any>({});
    const [permissions, setPermissions] = useState<any>([]);
    const [addUpdateModal, setAddUpdateModal] = useState<boolean>(false);
    const [submitLoading, setSubmitLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<any>({});
    const [editId, setEditId] = useState<any>(null);

    useEffect(() => {
        getUsersList();
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    // Get users list
    const getUsersList = async () => {
        setPageLoad(false);

        const response = await getAllDetails(LIST_USER);
        // get response
        if (response) {
            const DataList = response?.data;
            if (DataList.length === 0) {
                setUserList([]);
            } else {
                setUserList(DataList);
            }
            setPageLoad(true);
        } else {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    // global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
                <Button label="New User" icon="pi pi-plus" className="p-button-primary ml-2 mr-2" onClick={() => openAddModal()} />
            </>
        );
    };

    // Open add modal
    const openAddModal = () => {
        setAddUpdateModal(true);
    };

    // Open edit modal
    const openEditModal = (rowData: any) => {
        setEditId(rowData.id);
        setAddUpdateModal(true);
    };

    // Close add update modal
    const closeAddUpdateModal = () => {
        setUserData({});
        setEditId(null);
        setErrors({});
        setSubmitLoading(false);
        setAddUpdateModal(false);
    };

    // for column action
    const actionBodyTemplate = (rowData: any) => {
        return (
            <div className="actions">
                <Button icon="pi pi-pencil" tooltip="Edit" tooltipOptions={{ position: 'top' }} className="p-button-rounded p-button-primary mr-3" onClick={() => openEditModal(rowData)} disabled={true} />
            </div>
        );
    };

    // for name Template
    const nameTemplate = (rowData: any) => {
        return (
            <>
                <div className="avatar-div flex align-items-center">
                    {!window.cn(rowData?.photo) ? <Avatar image={rowData?.photo} shape="circle" /> : <Avatar image={'/assets/images/avatar.png'} shape="circle" />}
                    <div className="emp-name ml-2">
                        {rowData.first_name} {rowData.last_name}
                    </div>
                </div>
            </>
        );
    };

    // for status template
    const statusTemplate = (rowData: any) => {
        let statusClass: any = rowData.status === 1 ? 'success' : 'danger';
        return (
            <div className="actions flex flex-column align-items-center">
                <InputSwitch className="mr-2 mb-2" value={rowData.status} checked={rowData?.status === 1 ? true : false} onChange={(e) => onStatusChange(rowData, e)} />
                <Badge severity={statusClass} value={rowData.status === 1 ? 'Active' : 'In Active'} style={{ width: 'fit-content' }}></Badge>
            </div>
        );
    };

    // On status change
    const onStatusChange = async (rowData: any, e: any) => {
        setPageLoad(false);

        let status = 0;
        if (e.target.value == true) {
            status = 1;
        }

        const response = await getAllDetails(CHANGE_STATUS_USER + '?id=' + rowData.id + '&status=' + status);

        // get response
        if (response) {
            getUsersList();
            toast.current?.show({
                severity: 'success',
                summary: 'Message',
                detail: response?.message
            });
            setPageLoad(true);
        } else {
            setPageLoad(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Alert',
                detail: 'Something went wrong, Please try again.'
            });
        }
    };

    // On input change event
    const onInputChange = (e: any, name: string) => {
        let val;
        if (name === 'photo') {
            val = e.target.files[0] || '';
        } else {
            val = (e.target && e.target.value) || e.value || '';
        }
        setUserData({ ...userData, [name]: val });
    };

    // On add update submit
    const onSubmit = async () => {
        try {
            const { errors, isError } = AddUpdateUserValidate(userData, permissions);
            setErrors(errors);

            if (!isError) {
                setSubmitLoading(true);

                let requestData = new FormData();

                if (editId !== null) {
                    requestData.append('id', editId);
                }
                requestData.append('first_name', userData?.first_name);
                requestData.append('last_name', userData?.last_name);
                requestData.append('mobile', userData?.mobile);
                requestData.append('user_permission', userData?.user_permission?.code);

                //apicall
                const response = await postAllData(ADD_UPDATE_USER, requestData);
                if (response) {
                    toast.current?.show({ severity: 'success', summary: 'Success', detail: response.message });
                    setTimeout(() => {
                        setUserData({});
                        setEditId(null);
                        setAddUpdateModal(false);
                        setSubmitLoading(false);
                        getUsersList();
                    }, 1000);
                }
            }
        } catch (error: any) {
            if (error.response) {
                //Spinner False and Set Toast
                setSubmitLoading(false);
                toast.current?.show({ severity: 'error', summary: 'Alert', detail: error.response.message });
            }
        }
    };

    return (
        <>
            <AppBreadcrumb currentMenu={'User Management'} pageName={'Users'} pageTitle={'Users'} />
            <div className="grid crud-demo expanded-table">
                <div className="col-12">
                    <div className="card">
                        <Toast ref={toast} />
                        <Toolbar left={leftToolbarTemplate}></Toolbar>
                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    className="datatable-responsive"
                                    value={userList}
                                    paginator={userList.length >= 10 && true}
                                    globalFilter={globalFilter}
                                    rows={defaultRowOptions}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                    emptyMessage="No Users Found"
                                    showGridlines
                                    stripedRows
                                >
                                    {UserColumns.map((col, i) => {
                                        if (col.field === 'action') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={actionBodyTemplate} />;
                                        } else if (col.field === 'full_name') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={nameTemplate} />;
                                        } else if (col.field === 'status') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={statusTemplate} />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} sortable filter />;
                                        }
                                    })}
                                </DataTable>
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {UserColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>

            <Dialog
                visible={addUpdateModal}
                style={{ width: '850px' }}
                header={editId !== null ? 'Update User Details' : 'Add New User'}
                modal
                footer={
                    <>
                        <Button label="Cancel" icon="pi pi-times" className="p-button-secondary mr-4" onClick={closeAddUpdateModal} />
                        <Button label="Save" icon="pi pi-check" loading={submitLoading} onClick={onSubmit} />
                    </>
                }
                onHide={closeAddUpdateModal}
            >
                <div className="p-fluid formgrid grid">
                    <div className="field col-12 md:col-6">
                        <label htmlFor="name">First Name *</label>
                        <div className="input-effect-div">
                            <InputText value={!window.cn(userData?.first_name) ? userData?.first_name : ''} autoComplete="off" id="name" onChange={(e) => onInputChange(e, 'first_name')} className={errors['first_name'] && 'p-invalid'} />
                            <span className="focus-bg"></span>
                        </div>
                        <small className="p-invalid-txt">{errors['first_name']}</small>
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="name">Last Name *</label>
                        <div className="input-effect-div">
                            <InputText value={!window.cn(userData?.last_name) ? userData?.last_name : ''} autoComplete="off" id="name" onChange={(e) => onInputChange(e, 'last_name')} className={errors['last_name'] && 'p-invalid'} />
                            <span className="focus-bg"></span>
                        </div>
                        <small className="p-invalid-txt">{errors['last_name']}</small>
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="mobile">Mobile *</label>
                        <div className="input-effect-div">
                            <InputText keyfilter="num" maxLength={10} value={!window.cn(userData.mobile) ? userData.mobile : ''} name="mobile" onChange={(e) => onInputChange(e, 'mobile')} id="mobile" className={errors['mobile'] && 'p-invalid'} />
                            <span className="focus-bg"></span>
                        </div>
                        <small className="p-invalid-txt">{errors['mobile']}</small>
                    </div>

                    <div className="field col-12 md:col-6">
                        <label htmlFor="user_permission">Permission(Web & Mobile App) *</label>
                        <Dropdown
                            filter
                            value={!window.cn(userData.user_permission) ? userData.user_permission : ''}
                            onChange={(e) => onInputChange(e, 'user_permission')}
                            options={[
                                { name: 'Web', code: 'Web' },
                                { name: 'App', code: 'App' },
                                { name: 'Both', code: 'Both' }
                            ]}
                            optionLabel="name"
                            name="user_permission"
                            placeholder="Select Permission"
                            className={errors['user_permission'] && 'p-invalid'}
                        ></Dropdown>
                        <small className="p-invalid-txt">{errors['user_permission']}</small>
                    </div>
                </div>
            </Dialog>
        </>
    );
};
